import * as React from "react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
  TextField,
  Tooltip,
  Typography,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { MoreHoriz } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import { Dashboard } from "../../store/types.tsx";
import DeleteIcon from "../icons/DeleteIcon.tsx";
import RenameIcon from "../icons/RenameIcon.tsx";
import {
  conversationActions,
  deleteDashboard,
  patchDashboard,
  selectDashboardAction,
} from "../../store/conversation.tsx";
import { styled } from "@mui/material/styles";

interface DashboardDrawerSectionProps {
  dashboards: Dashboard[];
  title: string;
  closeDrawer: () => void;
  onDashboardSelected?: () => void;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.text.primary,
    maxWidth: 200,
    margin: 0,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: "0px 4px 10px rgba(0,0,0,0.25)",
  },
}));

const DashboardDrawerSection: React.FC<DashboardDrawerSectionProps> = ({
  dashboards,
  title,
  closeDrawer,
  onDashboardSelected,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [openTooltip, setOpenTooltip] = React.useState(false);
  const [menuDashboard, setMenuDashboard] = React.useState<Dashboard | null>(
    null,
  );
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState("");
  const { selectedDashboard, selectedView } = useTypedSelector(
    (state) => state.conversationState,
  );
  const htmlTooltipListItemProps: SxProps = [
    {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(14),
      minHeight: 20,
      paddingY: theme.spacing(1),
    },
    {
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius / 2,
      },
    },
  ];

  const handleSelectDashboard = (dashboardId: string) => {
    closeDrawer();
    onDashboardSelected?.();
    dispatch(conversationActions.setSelectedView("dashboard"));
    dispatch(selectDashboardAction(dashboardId) as any);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    dashboard: Dashboard,
  ) => {
    event.stopPropagation();
    setMenuDashboard(dashboard);
    setOpenTooltip((current) => !current);
  };

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleOpenRename = () => {
    if (!menuDashboard) return;
    setRenameValue(menuDashboard.title);
    setRenameOpen(true);
    setOpenTooltip(false);
  };

  const handleConfirmRename = async () => {
    if (!menuDashboard || renameValue.trim() === "") {
      return;
    }

    await dispatch(patchDashboard(menuDashboard.id, renameValue.trim()) as any);
    setRenameOpen(false);
    setMenuDashboard(null);
  };

  const handleOpenDelete = () => {
    setDeleteOpen(true);
    setOpenTooltip(false);
  };

  const handleConfirmDelete = async () => {
    if (!menuDashboard) return;

    await dispatch(deleteDashboard(menuDashboard.id) as any);
    setDeleteOpen(false);
    setMenuDashboard(null);
  };

  const handleCloseDialogs = () => {
    setRenameOpen(false);
    setDeleteOpen(false);
    setMenuDashboard(null);
  };

  return (
    <Box className="conversation-drawer-section">
      <h4>{title}</h4>
      <Box sx={{ clear: "both" }}>
        {dashboards.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "rgba(0, 0, 0, 0.65)",
              py: "6px",
            }}
          >
            {i18n._(t`No dashboards yet`)}
          </Typography>
        ) : (
          <List>
            {dashboards.map((dashboard) => {
              const isSelected =
                selectedView === "dashboard" &&
                selectedDashboard === dashboard.id;

              return (
                <ListItem
                  key={dashboard.id}
                  disablePadding
                  sx={{
                    borderRadius: "25px",
                    transition: "all 0.2s ease",
                    overflow: "visible",
                  }}
                >
                  <Tooltip
                    title={
                      dashboard.tile_count === 1
                        ? i18n._(t`${dashboard.tile_count} tile`)
                        : i18n._(t`${dashboard.tile_count} tiles`)
                    }
                    arrow
                    placement="right"
                  >
                    <Box
                      className="conversation-section-full-width"
                      sx={[
                        {
                          ml: "-10px",
                          mr: "-10px",
                          borderRadius: "25px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "calc(100% + 6px)",
                        },
                        isSelected && {
                          backgroundColor: "rgba(0, 0, 0, 0.20)",
                          color: theme.palette.common.black,
                          borderRadius: "14px",
                        },
                      ]}
                    >
                      <ListItemButton
                        disableRipple
                        onClick={() => handleSelectDashboard(dashboard.id)}
                        sx={{
                          flex: 1,
                          borderRadius: "25px",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <ListItemText
                          primary={dashboard.title}
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "& .MuiListItemText-primary": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      </ListItemButton>
                      {isSelected && (
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <Box>
                            <HtmlTooltip
                              onClose={handleTooltipClose}
                              title={
                                <React.Fragment>
                                  <List
                                    sx={{
                                      margin: 0,
                                      padding: 0,
                                    }}
                                  >
                                    <ListItemButton
                                      sx={htmlTooltipListItemProps}
                                      onClick={handleOpenRename}
                                    >
                                      <RenameIcon />
                                      {i18n._(t`Rename`)}
                                    </ListItemButton>
                                    <ListItemButton
                                      sx={htmlTooltipListItemProps}
                                      onClick={handleOpenDelete}
                                    >
                                      <DeleteIcon />
                                      {i18n._(t`Delete`)}
                                    </ListItemButton>
                                  </List>
                                </React.Fragment>
                              }
                              placement="bottom-start"
                              disableFocusListener
                              disableHoverListener
                              disableTouchListener
                              open={openTooltip}
                            >
                              <ListItemButton
                                onClick={(event) =>
                                  handleOpenMenu(event, dashboard)
                                }
                                sx={{
                                  float: "right",
                                  width: "1.5rem",
                                  display: "flex",
                                  ".MuiListItem-root:hover &": {
                                    display: "flex",
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={[
                                    {
                                      color: theme.palette.primary.main,
                                    },
                                  ]}
                                >
                                  <MoreHoriz />
                                </ListItemIcon>
                              </ListItemButton>
                            </HtmlTooltip>
                          </Box>
                        </ClickAwayListener>
                      )}
                    </Box>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      <Dialog
        open={renameOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{i18n._(t`Rename dashboard`)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={i18n._(t`Dashboard title`)}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{i18n._(t`Cancel`)}</Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={renameValue.trim() === ""}
          >
            {i18n._(t`Save`)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{i18n._(t`Delete dashboard?`)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {menuDashboard
              ? i18n._(
                  t`This will remove "${menuDashboard.title}" and all of its tiles.`,
                )
              : i18n._(
                  t`This will remove this dashboard and all of its tiles.`,
                )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{i18n._(t`Cancel`)}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            {i18n._(t`Delete`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardDrawerSection;
